import { Box, Button, Flex, Heading } from '@chakra-ui/react'
import type { ReactNode } from 'react'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams, useSearchParams } from 'react-router'
import { extensionsApi } from '~/apis/extensionsApi'
import { useUserState } from '~/atoms/UserState'
import { toaster } from '~/components/ui/toaster'
import { useCheckRole } from '~/hooks/useCheckRole'
import { Environment } from '~/types/EnvironmentTypes'
import { ApplicationRoutes } from '~/types/RoutingTypes'
import { getApiEnvironment } from '~/utils/common'

import {
  EXTENSION_TYPE_EDIT_SLUG,
  ExtensionEditByTypePage,
  ExtensionEditPlaceholderPage,
  ExtensionsListPage,
  TYPE_SLUG_TO_VIEW_MODE,
} from '@mangovoice/extensions-ui'
import type { ExtensionForDisplay } from '@mangovoice/extensions-ui'
import { ExtensionType } from '@mangovoice/types'

const LIST_VIEW_MODES = [
  'all',
  'lines',
  'ring-groups',
  'call-queues',
  'conference-rooms',
  'virtual-fax',
  'shared-voicemail',
] as const

type ExtensionListViewMode = (typeof LIST_VIEW_MODES)[number]

const VIEW_TABS: Array<{
  view: Exclude<ExtensionListViewMode, 'all'>
  labelKey: string
  fallback: string
}> = [
  {
    view: 'lines',
    labelKey: 'extensions.viewModes.lines',
    fallback: 'Lines',
  },
  {
    view: 'ring-groups',
    labelKey: 'extensions.viewModes.ringGroups',
    fallback: 'Ring Groups',
  },
  {
    view: 'call-queues',
    labelKey: 'extensions.viewModes.callQueues',
    fallback: 'Call Queues',
  },
  {
    view: 'conference-rooms',
    labelKey: 'extensions.viewModes.conferenceRooms',
    fallback: 'Conference Rooms',
  },
  {
    view: 'virtual-fax',
    labelKey: 'extensions.viewModes.virtualFax',
    fallback: 'Virtual Fax',
  },
  {
    view: 'shared-voicemail',
    labelKey: 'extensions.viewModes.sharedVoicemail',
    fallback: 'Shared Voicemail',
  },
]

const VALID_LIST_VIEW_MODES = new Set<string>(LIST_VIEW_MODES)

const WebAppExtensionEditWrapper = ({ children }: { children: ReactNode }) => {
  return (
    <div
      style={{
        height: '100%',
        minHeight: 0,
        overflowY: 'auto',
      }}
    >
      {children}
    </div>
  )
}

export const ExtensionsPage = () => {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const { pbxId, userRoles } = useUserState({ canAutoRefresh: false })
  const checkRole = useCheckRole()
  const isInternalUser = Boolean(userRoles?.is_staff || userRoles?.is_superuser)
  const [searchParams, setSearchParams] = useSearchParams()
  const { type, extensionId } = useParams<{
    type?: string
    extensionId?: string
  }>()
  const safePbxId = pbxId ?? 0
  const viewParam = searchParams.get('view')
  // Used only as `useState` initializer inside the library, so we don't
  // memoize: `useSearchParams` returns a new object every render, making a
  // memo misleading, and subsequent changes here would be ignored anyway.
  const initialAssignedUserFilter = [
    ...searchParams.getAll('assignedUserFilter'),
    ...searchParams.getAll('assignedUserFilter[]'),
  ]
    .flatMap((value) => value.split(','))
    .map((value) => parseInt(value, 10))
    .filter((value) => Number.isFinite(value) && value > 0)
  const isValidViewParam =
    viewParam != null && VALID_LIST_VIEW_MODES.has(viewParam)
  const activeTabView: Exclude<ExtensionListViewMode, 'all'> =
    viewParam === 'all'
      ? 'lines'
      : isValidViewParam
        ? (viewParam as Exclude<ExtensionListViewMode, 'all'>)
        : 'lines'

  useEffect(() => {
    if (type != null && extensionId != null) return
    if (isValidViewParam) return

    const next = new URLSearchParams(searchParams)
    next.set('view', 'lines')
    setSearchParams(next, { replace: true })
  }, [extensionId, isValidViewParam, searchParams, setSearchParams, type])

  const handleSelectTab = (view: Exclude<ExtensionListViewMode, 'all'>) => {
    navigate(`${ApplicationRoutes.EXTENSIONS}?view=${view}`)
  }

  const WebAppExtensionsListWrapper = ({
    children,
  }: {
    children: ReactNode
  }) => {
    return (
      <Flex h="full" minH={0} direction="column">
        <Box bg="bg.panel" px={4} pt={4}>
          <Heading size="xl" mb={2}>
            {t('nav.extensions', 'Extensions')}
          </Heading>
          <Flex
            role="tablist"
            aria-label={t('nav.extensions', 'Extensions')}
            borderBottom="1px solid"
            borderColor="border.muted"
            gap={6}
            overflowX="auto"
          >
            {VIEW_TABS.map((tab) => {
              const isActive = activeTabView === tab.view
              return (
                <Button
                  key={tab.view}
                  role="tab"
                  aria-selected={isActive}
                  variant="ghost"
                  size="sm"
                  px={0}
                  py={3}
                  borderRadius={0}
                  color={isActive ? 'fg.default' : 'fg.muted'}
                  fontWeight={isActive ? 'semibold' : 'normal'}
                  borderBottom="2px solid"
                  borderBottomColor={isActive ? 'brand.solid' : 'transparent'}
                  _hover={{
                    color: 'fg.default',
                    bg: 'transparent',
                  }}
                  onClick={() => handleSelectTab(tab.view)}
                >
                  {t(tab.labelKey, tab.fallback)}
                </Button>
              )
            })}
          </Flex>
        </Box>
        <Box flex={1} minH={0} overflowY="auto" bg="bg.subtle" p={4}>
          <Box bg="bg.panel" borderRadius="lg" minH={0}>
            {children}
          </Box>
        </Box>
      </Flex>
    )
  }

  if (!safePbxId) return null

  if (type != null && extensionId != null) {
    return (
      <ExtensionEditByTypePage
        api={extensionsApi}
        pbxId={safePbxId}
        type={type}
        extensionId={extensionId}
        isMangoInternal={isInternalUser}
        isUkEnvironment={getApiEnvironment() === Environment.PRODUCTION_UK}
        toast={toaster}
        getListRoute={({ type: routeType }) => {
          const viewMode = TYPE_SLUG_TO_VIEW_MODE[routeType]
          return viewMode
            ? `${ApplicationRoutes.EXTENSIONS}?view=${viewMode}`
            : ApplicationRoutes.EXTENSIONS
        }}
        getEditRoute={({ type: routeType, extensionId: routeExtensionId }) =>
          ApplicationRoutes.EXTENSION_EDIT.replace(':type', routeType).replace(
            ':extensionId',
            String(routeExtensionId),
          )
        }
        wrapper={WebAppExtensionEditWrapper}
        t={t}
        locale={i18n.resolvedLanguage ?? i18n.language}
        fallback={({ type: unknownType }) => (
          <ExtensionEditPlaceholderPage
            type={unknownType}
            listRoute={ApplicationRoutes.EXTENSIONS}
          />
        )}
      />
    )
  }

  return (
    <ExtensionsListPage
      api={extensionsApi}
      pbxId={safePbxId}
      actionsEnabled={checkRole('admin')}
      t={t}
      locale={i18n.resolvedLanguage ?? i18n.language}
      userRoles={userRoles}
      toast={toaster}
      viewParam={viewParam}
      initialAssignedUserFilter={initialAssignedUserFilter}
      wrapper={WebAppExtensionsListWrapper}
      getEditRoute={(_pbxIdStr: string, extension: ExtensionForDisplay) => {
        if (extension.type === ExtensionType.DIAL_PLAN) {
          return ApplicationRoutes.SWITCHBOARD_EDIT.replace(
            ':extensionDialPlan',
            String(extension.id),
          )
        }
        const slug =
          EXTENSION_TYPE_EDIT_SLUG[
            extension.type as Exclude<ExtensionType, 'ExtensionDialPlan'>
          ] || 'line'
        return ApplicationRoutes.EXTENSION_EDIT.replace(':type', slug).replace(
          ':extensionId',
          String(extension.id),
        )
      }}
      getNewRoute={(_pbxIdStr: string, typeSlug: string) =>
        ApplicationRoutes.EXTENSION_EDIT.replace(':type', typeSlug).replace(
          ':extensionId',
          'new',
        )
      }
    />
  )
}
